import { RateInterval } from '__types__/enums';
import {
  AlertTriangle,
  CalendarDays,
  Clock,
  CreditCard,
  type LucideIcon,
  Package,
  Tag,
  Unlink
} from 'lucide-react';
import { ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { RateCardOption } from 'v5/core/entity-schema/entitySchema.types';
import { rateCardFormValue } from 'v5/core/entity-values/codecs/rate.codec';
import type { RateFormValue } from 'v5/core/entity-values/values.types';
import { describeRate, type RateDescription } from 'v5/core/rates/describeRate';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { cn } from 'v5/platform/dom/cn';
import { fieldAnchor } from '../../unitPath';
import { useRateCopy } from './useRateCopy';

/** A custom rate's part, marked as in its editor; a rate card's is not. */
function partAnchor(path: string | null, part: string) {
  return path === null ? {} : fieldAnchor(`${path}.${part}`);
}

/**
 * One rate as a line of the list: a tile with its billing unit's icon, then
 * name, amount, fees (as chips) and note. From 320px of list width the
 * amount moves right, in tabular figures, so amounts line up down the list
 * like a price column. Abbreviations are visual only; screen readers get
 * "850 Canadian dollars per day, plus 15% agency fee".
 */
function RateSummaryLayout({
  value,
  path,
  badge,
  testId
}: {
  value: RateFormValue;
  path: string | null;
  badge: ReactNode;
  testId: string;
}) {
  const copy = useRateCopy();
  const rate = describeRate(value, copy);
  return (
    <div data-testid={testId} className="flex min-w-0 items-start gap-3">
      <RateTile icon={INTERVAL_ICONS[value.interval]} />
      <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)] gap-x-4 gap-y-1 @min-[320px]:grid-cols-[minmax(0,1fr)_auto]">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className="font-semibold break-words text-fg-default"
            {...partAnchor(path, 'name')}
          >
            {rate.name}
          </span>
          {badge}
        </div>
        <div
          className="whitespace-nowrap tabular-nums text-fg-default @min-[320px]:col-start-2 @min-[320px]:row-start-1 @min-[320px]:text-right"
          {...partAnchor(path, 'amount')}
        >
          <RateMoney money={rate.money} />
        </div>
        <RateExtras
          rate={rate}
          path={path}
          className="@min-[320px]:col-span-2"
        />
      </div>
    </div>
  );
}

/** What a rate is billed by, at a glance: day, hour, flat fee or item. */
const INTERVAL_ICONS: Record<RateInterval, LucideIcon> = {
  [RateInterval.Daily]: CalendarDays,
  [RateInterval.Hourly]: Clock,
  [RateInterval.Fixed]: Tag,
  [RateInterval.Item]: Package
};

/** Anchors a rate in the list; the unit it shows is also said in words. */
function RateTile({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span
      aria-hidden="true"
      className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border-default bg-bg-subtle text-fg-muted"
    >
      <Icon className="size-4" />
    </span>
  );
}

/** "USD 850 / day", said in full to screen readers. */
export function RateMoney({ money }: { money: RateDescription['money'] }) {
  const copy = useRateCopy();
  if (money.type === 'none') {
    return <span className="text-fg-muted italic">{copy.noAmount}</span>;
  }
  return (
    <>
      <span aria-hidden="true">
        <span className="mr-1 text-sm font-semibold tracking-wide text-fg-muted">
          {money.currency}
        </span>
        <span className="font-semibold">{money.amount}</span>
        <span className="ml-1 text-fg-muted">{money.unit}</span>
      </span>
      <span className="sr-only">{money.spoken}</span>
    </>
  );
}

/** The fees and the note under a rate's amount. */
function RateExtras({
  rate,
  path,
  className
}: {
  rate: RateDescription;
  path: string | null;
  className?: string;
}) {
  return (
    <>
      {rate.feesSpoken !== null && (
        <div className={cn('flex flex-wrap gap-1', className)}>
          {rate.fees.map(({ fee, text }) => (
            <Badge
              key={fee.column}
              variant="gray"
              tone="subtle"
              size="sm"
              className="font-medium tabular-nums"
              aria-hidden="true"
              {...partAnchor(path, fee.column)}
            >
              {text}
            </Badge>
          ))}
          <span className="sr-only">{rate.feesSpoken}</span>
        </div>
      )}
      {rate.note !== '' && (
        <div
          className={cn('line-clamp-2 text-sm text-fg-muted italic', className)}
          {...partAnchor(path, 'description')}
        >
          {rate.note}
        </div>
      )}
    </>
  );
}

/** A custom rate (no `rate_id`) in display mode. */
export function CustomRateSummary({ path }: { path: string }) {
  const value = useWatch<Record<string, RateFormValue>>({ name: path });
  return (
    <RateSummaryLayout
      value={value}
      path={path}
      badge={null}
      testId="rate-summary"
    />
  );
}

/**
 * A rate-card row (`rate_id` set): the card is the source of truth, so the
 * row shows the card's values and is never edited here. `card` is `null`
 * when the card is archived or the user cannot read rate cards.
 */
export function RateCardSummary({
  rateId,
  card
}: {
  rateId: number;
  card: RateCardOption | null;
}) {
  const { t } = useTranslation('core');
  if (card === null) {
    return (
      <div
        data-testid="rate-card-summary"
        className="flex min-w-0 items-center gap-3"
      >
        <RateTile icon={CreditCard} />
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="font-semibold text-fg-default">
            {t('rateEditor.rateCardFallbackName', { id: rateId })}
          </span>
          <RateCardUnavailableBadge />
          <span className="sr-only">
            {t('rateEditor.rateCardUnavailableHint')}
          </span>
        </div>
      </div>
    );
  }
  return (
    <RateSummaryLayout
      value={rateCardFormValue(card)}
      path={null}
      testId="rate-card-summary"
      badge={<RateCardBadge />}
    />
  );
}

export function RateCardBadge() {
  const { t } = useTranslation('core');
  return (
    <SimpleTooltip title={t('rateEditor.rateCardHint')}>
      <Badge variant="info" tone="subtle" size="sm">
        <CreditCard aria-hidden="true" />
        {t('rateEditor.rateCard')}
      </Badge>
    </SimpleTooltip>
  );
}

export function RateCardUnavailableBadge() {
  const { t } = useTranslation('core');
  return (
    <SimpleTooltip title={t('rateEditor.rateCardUnavailableHint')}>
      <Badge variant="attention" tone="subtle" size="sm">
        <AlertTriangle aria-hidden="true" />
        {t('rateEditor.rateCardUnavailable')}
      </Badge>
    </SimpleTooltip>
  );
}

/**
 * A rate-card row while its list is edited: its header names it and shows
 * the amount (`RateRowHeading`), so the row says what the header can't —
 * the fees, the note and where the card is managed. "Detach and edit" turns
 * it into a custom rate holding the card's values, so it can be changed
 * here; an unavailable card has no values to copy.
 */
export function RateCardTerms({
  card,
  path
}: {
  card: RateCardOption | null;
  path: string;
}) {
  const { t } = useTranslation('core');
  const copy = useRateCopy();
  const form = useFormContext<Record<string, RateFormValue>>();
  return (
    <div data-testid="rate-card-terms" className="flex flex-col gap-0.5">
      {card !== null && (
        <RateExtras
          rate={describeRate(rateCardFormValue(card), copy)}
          path={null}
        />
      )}
      <p className="text-sm text-fg-muted">
        {card === null
          ? t('rateEditor.rateCardUnavailableHint')
          : t('rateEditor.rateCardHint')}
      </p>
      {card !== null && (
        <Button
          variant="secondary"
          size="xs"
          className="mt-1.5 self-start"
          onClick={() =>
            form.setValue(
              path,
              { ...rateCardFormValue(card), rate_id: null },
              { shouldDirty: true, shouldValidate: true }
            )
          }
        >
          <Unlink aria-hidden />
          {t('rateEditor.detachAndEdit')}
        </Button>
      )}
    </div>
  );
}
